import React from 'react';
import { Route, Switch } from 'react-router-dom';
import { styled } from '@mui/material';
import { useAppSelector, useLocation } from 'src/utils/Hooks';
import { getLoginContainerConfig } from 'src/components/SignIn/SignInContainer';
import { MarketingAuthRoutes, MarketingSiteContainer, MarketingIframe } from 'src/routes/routeTable_v2';
import { getEnterpriseSsoHost, getSsoReferrer, isSsoHost } from 'src/utils/ssoReferrer';

const SignInV2Container = React.lazy(() => import('src/components/SignIn/SignInV2Container'));
const AuthCallback = React.lazy(() => import('src/components/SignIn/AuthCallback'));
const SignOut = React.lazy(() => import('src/components/SignOut'));
const SwitchDivision = React.lazy(() => import('src/components/SignIn/SwitchDivision'));
const NoAccessRedirect = React.lazy(() => import('src/components/SignIn/NoAccessRedirect'));

const Root = styled('div')({
  height: '100%'
});

/**
 * Lightweight, V1/V2-agnostic shell for auth routes.
 *
 * Mounted by `main.tsx` for the auth paths before any V1/V2 shell
 * decision is made. The decision is deferred until after the form submits
 * (we now know the email and Split.io can decide). The API issues either
 * v1 or v2 session cookies plus a non-HttpOnly `sl-auth-version` marker;
 * the submit handler triggers a full reload, which remounts `main.tsx` ->
 * not an auth route -> AppSwitch reads the marker and picks the correct
 * heavy shell on its first evaluation.
 *
 * Rendering rules:
 * - `/` on an SSO host (signin / enterprise-sso) -> SignInV2Container
 *   (canonical sign-in URL — `/sign_in` is redundant when the host already
 *   encodes sign-in intent)
 * - `/sign_in` on app domain -> redirect to `${ssoHost}/`
 * - `/sign_in` on an SSO host -> SignInV2Container (backward-compat alias
 *   of `/` for bookmarks and in-flight OAuth)
 * - `/switch-division` and `/no-access` -> redirect to SSO host when
 *   accessed from app domain; render directly on SSO host
 * - Everything else -> reuses the existing components from routeTable_v2
 */
const AuthShell: React.FC = () => {
  const location = useLocation();
  const app = useAppSelector((state) => state.app);
  const isOnSsoHost = isSsoHost(window.location.host);

  // App-domain pre-login: kick the user to the unified signin login.
  // Both `/sign_in` and `/` (when arrived at on the app domain via some
  // stray link) bounce to the canonical SSO root.
  if (!isOnSsoHost && location.pathname === '/sign_in') {
    const referrer = getSsoReferrer();
    const returnUrl = encodeURIComponent(window.location.origin + '/');
    window.location.href = `https://${getEnterpriseSsoHost()}/?referrer=${referrer}&returnUrl=${returnUrl}`;
    return null;
  }

  const renderSignIn = (renderProps: any) => {
    if (isOnSsoHost) {
      const backgroundColor = getLoginContainerConfig(app.name).bgColor;
      return (
        <div style={{ display: 'flex', height: '100%' }}>
          <MarketingSiteContainer className="signin_marketing">
            <MarketingIframe />
          </MarketingSiteContainer>
          <div className="signin_container" style={{ flex: 1, minWidth: '50%', zIndex: 0, backgroundColor }}>
            <SignInV2Container {...renderProps} />
          </div>
        </div>
      );
    }
    return <MarketingAuthRoutes {...renderProps} app={app} />;
  };

  return (
    <Root>
      <React.Suspense fallback={null}>
        <Switch>
          {/*
            On an SSO host, `/` is the canonical sign-in URL. `/sign_in` is
            kept as a backward-compat alias so bookmarks and in-flight OAuth
            flows continue to work.
          */}
          {isOnSsoHost && (
            <Route exact path="/" render={renderSignIn} />
          )}

          <Route exact path="/sign_in" render={renderSignIn} />

          <Route exact path="/auth/callback" component={AuthCallback} />

          <Route
            exact
            path="/password_reset"
            render={(renderProps) => <MarketingAuthRoutes {...renderProps} app={app} />}
          />
          <Route
            exact
            path="/sign_up_invite"
            render={(renderProps) => <MarketingAuthRoutes {...renderProps} app={app} />}
          />

          <Route exact path="/sign_out" component={SignOut} />

          <Route
            exact
            path="/switch-division"
            render={(renderProps) => {
              if (isOnSsoHost) {
                const backgroundColor = getLoginContainerConfig(app.name).bgColor;
                return (
                  <div style={{ display: 'flex', height: '100%' }}>
                    <MarketingSiteContainer className="signin_marketing">
                      <MarketingIframe />
                    </MarketingSiteContainer>
                    <div className="signin_container" style={{ flex: 1, minWidth: '50%', zIndex: 0, backgroundColor }}>
                      <SwitchDivision {...renderProps} />
                    </div>
                  </div>
                );
              }
              window.location.href = `https://${getEnterpriseSsoHost()}/switch-division${window.location.search}`;
              return null;
            }}
          />

          <Route
            exact
            path="/no-access"
            render={(renderProps) => {
              if (isOnSsoHost) {
                return <NoAccessRedirect {...renderProps} />;
              }
              window.location.href = `https://${getEnterpriseSsoHost()}/no-access${window.location.search}`;
              return null;
            }}
          />
        </Switch>
      </React.Suspense>
    </Root>
  );
};

export default AuthShell;
